'use client';
import { useGetCloudStorageProvider } from '@unifyapps/network/generated/file-rest-api/file-rest-api';
import { addTenantPrefix } from '@unifyapps/network/utils/tenantBasePath';
import getDomain from '@unifyapps/network/utils/url/getDomain';
import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';
// biome-ignore lint/style/noRestrictedImports: useAsync from react-use for lazy loading
import { useAsync } from 'react-use';
import type { CloudStorageProviderDetails } from '../../hooks/useUppy';
import { UppyContext } from '../../hooks/useUppy';
import type { CompanionOptions } from '../../hooks/useUppy/getUppyInstance';

const getUppyLib = async () => {
  const getUppyInstance = await import('../../hooks/useUppy/getUppyInstance');

  return { getUppyInstance };
};

export const useUppyLazyGetInstance = () => {
  const { value, loading } = useAsync(getUppyLib, []);

  return { getUppyInstance: value?.getUppyInstance.default, isReady: !loading };
};

type UppyProviderProps = PropsWithChildren<CompanionOptions & { canMakeRequest: boolean }>;

export default function UppyProvider(props: UppyProviderProps) {
  const { queryMeta, children, canMakeRequest } = props;
  const { getUppyInstance, isReady } = useUppyLazyGetInstance();
  const basePath = queryMeta?.basePath;

  const { data: cloudStorageProviderDetails, isLoading: isCloudStorageProviderLoading } =
    useGetCloudStorageProvider<CloudStorageProviderDetails>({
      query: {
        enabled: Boolean(canMakeRequest),
        staleTime: Infinity,
      },
    });

  // reinitialize uppy on user refetch
  const uppyInstance = useMemo(() => {
    let baseUrl = getDomain(queryMeta);
    if (baseUrl) {
      baseUrl = addTenantPrefix(baseUrl, basePath);
    }

    return isReady && !isCloudStorageProviderLoading && baseUrl
      ? {
          loading: false,
          Uppy:
            getUppyInstance?.(`${baseUrl}/api/uploads`, {
              queryMeta,
              cloudStorageProviderDetails,
            }) ?? null,
        }
      : { loading: true, Uppy: null };
  }, [
    basePath,
    getUppyInstance,
    isReady,
    queryMeta,
    isCloudStorageProviderLoading,
    cloudStorageProviderDetails,
  ]);

  const value = useMemo(() => {
    return {
      uppyInstance,
      cloudStorageProviderDetails,
    };
  }, [uppyInstance, cloudStorageProviderDetails]);

  return <UppyContext.Provider value={value}>{children}</UppyContext.Provider>;
}
